import type { DerivedFeatureV1 } from '@/sdk/model/DerivedFeatureV1';
import type { FeatureRuleV1 } from '@/sdk/model/FeatureRuleV1';
import type { ModelVersionOutputV1 } from '@/sdk/model/ModelVersionOutputV1';
import type { ModelInput } from '@/tools/models/model.store';

/** Available model types used throughout model tools and scaling tables */
export const MODEL_TYPES = {
  TOOL: 'TOOL',
  SCALING: 'SCALING',
} as const;

/** Version and draft metadata enriched onto model list items for Enterprise users with Locking. */
export type ModelVersionMetadata = {
  /** Whether a draft exists for the current user */
  hasDraft: boolean;
  /** The versionId of the current user's draft, if one exists */
  draftVersionId?: string;
  /** The production version number (e.g. 1, 2, 3) or undefined if no versions exist */
  productionVersionNumber?: number;
  /** Total number of published versions */
  versionCount: number;
  /** Whether the production version is locked (true if any versions exist, since publishing locks the draft) */
  isLocked: boolean;
  /** Published versions, sorted newest first */
  versions: ModelVersionOutputV1[];
};

/** Union type of supported model type values from `MODEL_TYPES`. */
export type ModelType = (typeof MODEL_TYPES)[keyof typeof MODEL_TYPES];

/** Feature type identifier used when tracking edits in model tools. */
export const EDITED_FEATURE_TYPE = 'FEATURE_ID';

/** Shape for identifying a model input by any of its known ID fields. */
export type ModelInputIds = { inputFeatureId?: string; referenceId?: string; id?: string };

/** Value object from the output select UI for adding outputs to a model. */
export type ModelOutputSelectValue = {
  id: string;
  name: string;
  itemType: string;
};

/** Formula parameter entry returned by the formulas API. */
type FormulaParameter = {
  name?: string;
  item?: {
    id?: string;
  };
};

/** Subset of formula metadata used to build formula creator rules for derived features. */
export type FormulaOutput = {
  name?: string;
  scopedTo?: string;
  formula?: string;
  parameters?: FormulaParameter[];
};

/** Dependency item returned from `getDependencies` for model outputs.
 * Used to build tool inputs and track parent relationships for dependency filtering.
 */
export type OutputDependencyItem = {
  id: string;
  name: string;
  type: string;
  ancestors?: Array<{ name?: string }>;
  parameterOf?: Array<{ id: string }>;
};

/** Newly added derived feature created in the UI (unsaved until model save). */
export type NewlyAddedDerivedFeature = {
  id?: string;
  referenceId?: string; // provided if the feature is an intermediate feature
  isOutput: boolean;
  name: string;
  rules?: FeatureRuleV1[];
};

/** Pairs a derived feature with its original index before sorting, used for dependency-based reordering. */
export type IndexedFeature = {
  feature: NewlyAddedDerivedFeature;
  oldIndex: number;
};

/** Newly added model input created in the UI (unsaved until model save). */
export type NewlyAddedInputFeature = ModelInput & {
  referenceId: string;
  id: string;
};

/** Extended derived feature type used in model tools to include selected item info for edited formula inputs. */
export type ModelDerivedFeature = DerivedFeatureV1 & {
  selectedItem?: { name: string; id: string };
};
